import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useSurvey }    from '../context/SurveyContext';
import { useSurveyApi } from '../hooks/useSurveyApi';
import { useGuardedRoute } from '../hooks/useGuardedRoute';
import {
    preferencesSchema,
    COMM_CHANNEL_OPTIONS,
    DAYS_OF_WEEK,
    CONTACT_TIME_OF_DAY, CONTACT_TIME_LABELS,
    APPT_TIME_OF_DAY,    APPT_TIME_LABELS,
    TECH_COMFORT_OPTIONS
} from '../schemas/survey.schema';

import PageShell       from '../components/layout/PageShell';
import FormCard        from '../components/layout/FormCard';
import ProgressBar     from '../components/survey/ProgressBar';
import ContactSection  from '../components/survey/ContactSection';
import QuestionBlock   from '../components/survey/QuestionBlock';
import ChipGroup       from '../components/ui/ChipGroup';
import RadioGroup      from '../components/ui/RadioGroup';
import Button          from '../components/ui/Button';
import ErrorBanner     from '../components/ui/ErrorBanner';
import { debugLog }    from '../utils/logger';

/**
 * PreferencesPage  (/preferences)
 *
 * Guarded — redirects to /validate if no session token.
 * Pre-fills contact fields from SurveyContext (set by verifyDob).
 * On submit → POST /survey/submit → navigates to /done.
 */
export default function PreferencesPage() {
    const isReady  = useGuardedRoute();
    const navigate = useNavigate();
    const { patientData, practiceName, clearSurveyState } = useSurvey();
    const { submitPreferences, loading, error, clearError } = useSurveyApi();
    const [formError, setFormError] = useState(null);

    const { register, handleSubmit, control, setError, clearErrors, formState: { errors } } = useForm({
        resolver: zodResolver(preferencesSchema),
        defaultValues: {
            phone             : '',
            email             : '',
            commChannel       : [],
            callPrefDayOfWeek : [],
            callPrefTimeOfDay : [],
            apptPrefTimeOfDay : [],
            techComfortLevel  : ''
        }
    });

    const isEmailRequired = !patientData?.email || patientData.email === 'No email found';

    useEffect(() => {
        debugLog('ui_preferences_page_mounted');
    }, []);

    const onSubmit = useCallback(async (data) => {
        clearError();
        setFormError(null);
        clearErrors('email');
        debugLog('ui_preferences_submit_started', {
            payloadKeys: Object.keys(data || {})
        });

        if (isEmailRequired && !data.email?.trim()) {
            const message = 'Email field is not updated. Please enter your email address.';
            setError('email', { type: 'manual', message });
            setFormError({ code: 'EMAIL_REQUIRED', message });
            debugLog('ui_preferences_submit_blocked', { reason: 'email_required' });
            return;
        }

        // Build API payload — only include fields that have values
        // phone/email only sent if the patient entered a new value
        const payload = {};
        if (data.phone?.trim())             payload.phone             = data.phone.trim();
        if (data.email?.trim())             payload.email             = data.email.trim();
        if (data.commChannel?.length)       payload.commChannel       = data.commChannel;
        if (data.callPrefDayOfWeek?.length) payload.callPrefDayOfWeek = data.callPrefDayOfWeek;
        if (data.callPrefTimeOfDay?.length) payload.callPrefTimeOfDay = data.callPrefTimeOfDay;
        if (data.apptPrefTimeOfDay?.length) payload.apptPrefTimeOfDay = data.apptPrefTimeOfDay;
        if (data.techComfortLevel)          payload.techComfortLevel  = data.techComfortLevel;

        try {
            await submitPreferences(payload);
            clearSurveyState();
            debugLog('ui_preferences_submit_succeeded');
            navigate('/done');
        } catch {
            debugLog('ui_preferences_submit_failed');
            // error displayed via ErrorBanner
        }
    }, [submitPreferences, clearSurveyState, navigate, clearError, clearErrors, setError, isEmailRequired]);

    // Build time of day chip options with labels
    const contactTimeOptions = CONTACT_TIME_OF_DAY.map(v => ({ value: v, label: CONTACT_TIME_LABELS[v] }));
    const apptTimeOptions    = APPT_TIME_OF_DAY.map(v => ({ value: v, label: APPT_TIME_LABELS[v] }));

    if (!isReady) return null;

    return (
        <PageShell practiceName={practiceName}>
            <ProgressBar step={2} />

            {/* Verified patient identity badge */}
            {patientData && (
                <div className="flex items-center gap-3 rounded-lg px-4 py-3 mb-3" style={{ background: 'white', border: '0.5px solid #d8eaf5' }}>
                    <div
                        className="flex items-center justify-center flex-shrink-0 rounded-full text-sm font-bold"
                        style={{ width: 38, height: 38, background: '#EAF4FB', border: '1px solid #c5dff0', color: '#1B2F7E' }}
                        aria-hidden="true"
                    >
                        {patientData.firstName?.[0]}{patientData.lastName?.[0]}
                    </div>
                    <div>
                        <p className="text-sm font-semibold" style={{ color: '#1B2F7E' }}>
                            {patientData.firstName} {patientData.lastName}
                        </p>
                        <p className="text-xs flex items-center gap-1" style={{ color: '#6b8fa8' }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2ECC8E" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                            Identity verified
                        </p>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Contact section */}
                <FormCard>
                    <ContactSection
                        currentPhone={patientData?.phone}
                        currentEmail={patientData?.email}
                        register={register}
                        errors={errors}
                        isEmailRequired={isEmailRequired}
                    />
                </FormCard>

                {/* Survey questions */}
                <FormCard>
                    <h2 className="text-sm font-bold mb-4 pb-2.5" style={{ color: '#1B2F7E', borderBottom: '0.5px solid #eaf4fb' }}>
                        Patient Communication Preferences
                    </h2>

                    {/* Q1 */}
                    <QuestionBlock number={1} question="Preferred Methods of Communication" hint="Check all that apply">
                        <Controller
                            name="commChannel"
                            control={control}
                            render={({ field }) => (
                                <ChipGroup
                                    options={COMM_CHANNEL_OPTIONS}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.commChannel?.message}
                                />
                            )}
                        />
                    </QuestionBlock>

                    <div className="my-3.5" style={{ borderTop: '0.5px solid #eaf4fb' }} />

                    {/* Q2 */}
                    <QuestionBlock number={2} question="How comfortable are you using a smartphone to schedule and participate in telemedicine visits?">
                        <Controller
                            name="techComfortLevel"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup
                                    options={TECH_COMFORT_OPTIONS}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.techComfortLevel?.message}
                                />
                            )}
                        />
                    </QuestionBlock>

                    <div className="my-3.5" style={{ borderTop: '0.5px solid #eaf4fb' }} />

                    {/* Q3 */}
                    <QuestionBlock number={3} question="Preferred Day(s) to Be Contacted" hint="Check all that apply">
                        <Controller
                            name="callPrefDayOfWeek"
                            control={control}
                            render={({ field }) => (
                                <ChipGroup
                                    options={DAYS_OF_WEEK}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.callPrefDayOfWeek?.message}
                                />
                            )}
                        />
                    </QuestionBlock>

                    <div className="my-3.5" style={{ borderTop: '0.5px solid #eaf4fb' }} />

                    {/* Q4 */}
                    <QuestionBlock number={4} question="What times of day are best for us to contact you?" hint="Check all that apply">
                        <Controller
                            name="callPrefTimeOfDay"
                            control={control}
                            render={({ field }) => (
                                <ChipGroup
                                    options={contactTimeOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.callPrefTimeOfDay?.message}
                                />
                            )}
                        />
                    </QuestionBlock>

                    <div className="my-3.5" style={{ borderTop: '0.5px solid #eaf4fb' }} />

                    {/* Q5 */}
                    <QuestionBlock number={5} question="What times of day are best for you to attend telehealth appointments?" hint="Check all that apply">
                        <Controller
                            name="apptPrefTimeOfDay"
                            control={control}
                            render={({ field }) => (
                                <ChipGroup
                                    options={apptTimeOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.apptPrefTimeOfDay?.message}
                                />
                            )}
                        />
                    </QuestionBlock>
                </FormCard>

                <ErrorBanner error={formError || error} onDismiss={formError ? () => setFormError(null) : clearError} />

                <Button type="submit" loading={loading}>
                    Submit
                </Button>
            </form>
        </PageShell>
    );
}